import { Box, Tooltip, rem } from '@mantine/core';
import type { EmotionSx } from '@mantine/emotion';
import { THEME_COLORS } from '@vrplatform/ui-common';
import type { ReactNode } from 'react';
import { VRPilotMark } from './VRPilotMark';

const INLINE_BADGE_SIZES = {
  xxs: {
    frame: 12,
    mark: 9,
    font: 9,
    padding: 4,
    radius: 3,
  },
  xs: {
    frame: 14,
    mark: 10,
    font: 10,
    padding: 5,
    radius: 4,
  },
  sm: {
    frame: 18,
    mark: 13,
    font: 11,
    padding: 6,
    radius: 5,
  },
  md: {
    frame: 22,
    mark: 16,
    font: 12,
    padding: 8,
    radius: 6,
  },
  lg: {
    frame: 26,
    mark: 19,
    font: 12,
    padding: 9,
    radius: 7,
  },
};

export const VRPilotBadge = ({
  ariaLabel = 'VRPilot',
  label,
  size = 'md',
  sx,
  tooltip,
}: {
  ariaLabel?: string;
  label?: ReactNode;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  sx?: EmotionSx;
  tooltip?: ReactNode;
}) => {
  const badgeSize = INLINE_BADGE_SIZES[size];
  const hasLabel = label !== undefined;

  return (
    <Tooltip disabled={!tooltip} label={tooltip} withArrow withinPortal>
      <Box
        aria-label={ariaLabel}
        component="span"
        role={hasLabel ? undefined : 'img'}
        sx={(theme, utils) => ({
          alignItems: 'center',
          background: hasLabel
            ? THEME_COLORS.violet[0]
            : THEME_COLORS.violet[6],
          border: hasLabel ? `1px solid ${THEME_COLORS.violet[2]}` : 0,
          borderRadius: hasLabel ? rem(999) : rem(badgeSize.radius),
          boxSizing: 'border-box',
          color: THEME_COLORS.violet[8],
          display: 'inline-flex',
          flex: '0 0 auto',
          fontSize: rem(badgeSize.font),
          fontWeight: 600,
          gap: hasLabel ? rem(4) : undefined,
          height: rem(hasLabel ? badgeSize.frame + 4 : badgeSize.frame),
          justifyContent: 'center',
          lineHeight: 1,
          maxWidth: '100%',
          overflow: 'hidden',
          paddingInline: hasLabel ? `${rem(1)} ${rem(badgeSize.padding)}` : 0,
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
          width: hasLabel ? undefined : rem(badgeSize.frame),
          ...(typeof sx === 'function' ? sx(theme, utils) : sx),
        })}
      >
        {hasLabel ? (
          <>
            <VRPilotBadge size={size} />
            <Box
              component="span"
              sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {label}
            </Box>
          </>
        ) : (
          <VRPilotMark size={badgeSize.mark} />
        )}
      </Box>
    </Tooltip>
  );
};
